import {Injectable} from '@angular/core';
import {onCLS, onINP, onLCP} from 'web-vitals';

// Lazy load Firebase plugins only when needed
// import {FirebaseAnalytics} from '@capacitor-firebase/analytics';
// import {FirebasePerformance} from '@capacitor-firebase/performance';
import {environment} from '../../../../environments/environment';

function isPromise(promise) {
  return !!promise && typeof promise.then === 'function';
}

@Injectable({
  providedIn: 'root',
})
export class GoogleAnalyticsService {
  traces: {name: string; time: number}[] = [];

  constructor() {
    this.logPerformanceMetrics();
  }

  get isSupported() {
    // Check if Firebase is enabled and measurementId exists
    const useFirebase = (environment as any).useFirebase !== false;
    return useFirebase && environment.firebase.measurementId && 'window' in globalThis && 'document' in globalThis;
  }

  async setCurrentScreen(screenName: string) {
    if (!this.isSupported) {
      return;
    }
    try {
      // Lazy load Firebase Analytics only when needed
      const {FirebaseAnalytics} = await import('@capacitor-firebase/analytics');
      await FirebaseAnalytics.setCurrentScreen({screenName});
    } catch (error: any) {
      // Silently fail if Firebase is not initialized
      console.warn('⚠️ Firebase Analytics not available:', error.message);
    }
  }

  logPerformanceMetrics() {
    if (!this.isSupported) {
      return;
    }

    const sendToGoogleAnalytics = async ({name, delta, value, id}) => {
      try {
        // Lazy load Firebase Analytics only when needed
        const {FirebaseAnalytics} = await import('@capacitor-firebase/analytics');
        await FirebaseAnalytics.logEvent({
          name,
          params: {
            value: delta,
            metric_id: id,
            metric_value: value,
            metric_delta: delta,
          },
        });
      } catch (error: any) {
        // Silently fail if Firebase is not initialized
        console.warn('⚠️ Firebase Analytics not available:', error.message);
      }
    };

    onCLS(sendToGoogleAnalytics);
    onINP(sendToGoogleAnalytics);
    onLCP(sendToGoogleAnalytics);
  }

  async trace<T>(timingCategory: string, timingVar: string, callable: () => T): Promise<T> {
    if (!this.isSupported) {
      return callable();
    }

    const startTime = performance.now();
    const traceName = `${timingCategory}:${timingVar}`;
    let firebasePerformance: any = null;
    try {
      // Lazy load Firebase Performance only when needed
      const {FirebasePerformance} = await import('@capacitor-firebase/performance');
      firebasePerformance = FirebasePerformance;
      await FirebasePerformance.startTrace({traceName});
    } catch (error: any) {
      // Silently fail if Firebase is not initialized
      console.warn('⚠️ Firebase Performance not available:', error.message);
    }
    const stopTrace = () => {
      this.traces.push({name: traceName, time: performance.now() - startTime});
      if (firebasePerformance) {
        firebasePerformance.stopTrace({traceName}).catch(() => {
          // Silently fail if Firebase is not initialized
        });
      }
    };

    let call = callable();
    if (isPromise(call)) {
      call = (call as any).then(async res => {
        stopTrace();
        return res;
      }) as any;
    } else {
      stopTrace();
    }

    return call;
  }
}
